/**
 * CRA Admin Dashboard
 *
 * Landing page for Credit Rating Agency Administrators.
 * Shows their CRA's institutions, team, reports, and pending approvals.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { useRoleAssumption } from '../../context/RoleAssumptionContext';
import {
  getUsersByOrg,
  getPendingUsersForOrg,
  getOrgReports,
  getOverdueReports,
  type UserProfile,
  type ReportWorkflow,
} from '../../services/firestore';
import { getRatedInstitutionsWithRatings } from '../../services/institutionService';
import './DashboardStyles.css';

interface InstitutionSummary {
  id: string;
  name: string;
  rating: string;
  score: number;
}

export default function CRAAdminDashboard() {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const { effectiveOrganization } = useRoleAssumption();
  const { t } = useTranslation();
  const [institutions, setInstitutions] = useState<InstitutionSummary[]>([]);
  const [teamMembers, setTeamMembers] = useState<UserProfile[]>([]);
  const [analysts, setAnalysts] = useState<UserProfile[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<UserProfile[]>([]);
  const [reports, setReports] = useState<ReportWorkflow[]>([]);
  const [overdueReports, setOverdueReports] = useState<ReportWorkflow[]>([]);
  const [loading, setLoading] = useState(true);

  const firstName = userProfile?.display_name?.split(' ')[0] || 'Admin';
  const orgName = effectiveOrganization?.name || 'Your CRA';

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Get institutions from BigQuery API
        // Filter by organization.institutions_access if available
        const institutionAccess = effectiveOrganization?.institutions_access;
        const ratedInstitutions = await getRatedInstitutionsWithRatings();

        const allInstitutions = ratedInstitutions
          .filter((inst) => !institutionAccess || institutionAccess.includes(inst.institution_id))
          .map((inst) => ({
            id: inst.institution_id,
            name: inst.institution_name,
            rating: inst.rating_category || 'N/A',
            score: inst.numeric_score || 0,
          }));
        setInstitutions(allInstitutions);

        // Get team members, pending approvals, and reports if org exists
        if (effectiveOrganization?.id) {
          const [users, pending, orgReports, overdue] = await Promise.all([
            getUsersByOrg(effectiveOrganization.id),
            getPendingUsersForOrg(effectiveOrganization.id),
            getOrgReports(effectiveOrganization.id),
            getOverdueReports(effectiveOrganization.id),
          ]);
          setTeamMembers(users);
          setAnalysts(users.filter((u) => u.role === 'cra_analyst'));
          setPendingApprovals(pending);
          setReports(orgReports);
          setOverdueReports(overdue);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [effectiveOrganization]);

  // Count reports by status
  const draftCount = reports.filter(r => r.status === 'draft').length;
  const pendingAdminCount = reports.filter(r => r.status === 'pending_admin').length;
  const clientReviewCount = reports.filter(r => r.status === 'client_review').length;
  const pendingCommitteeCount = reports.filter(r => r.status === 'pending_committee').length;

  const getRatingClass = (rating: string): string => {
    const r = rating.toLowerCase().replace('+', '').replace('-', '');
    return `rating-${r}`;
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>{t('dashboard.welcomeBack', { name: firstName })}</h1>
        <p className="dashboard-subtitle">{orgName} - {t('dashboard.craSubtitle')}</p>
      </div>

      <div className="dashboard-content">
        {/* Quick Actions */}
        <div className="dashboard-section">
          <h2 className="section-title">{t('dashboard.quickActions')}</h2>
          <div className="actions-grid">
            <button className="action-card primary" onClick={() => navigate('/reports')}>
              <span className="action-label">{t('dashboard.reports')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/admin/inventory')}>
              <span className="action-label">{t('dashboard.manageInventory')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/analyst-bench')}>
              <span className="action-label">{t('dashboard.analystBench')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/ratings')}>
              <span className="action-label">{t('dashboard.allRatings')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/admin/users')}>
              <span className="action-label">{t('dashboard.manageUsers')}</span>
            </button>
            <button className="action-card" onClick={() => navigate('/settings')}>
              <span className="action-label">{t('settings.title')}</span>
            </button>
          </div>
        </div>

        {/* CRA Stats */}
        <div className="dashboard-section">
          <h2 className="section-title">{t('dashboard.agencyOverview')}</h2>
          <div className="stats-grid">
            <div
              className="stat-card"
              onClick={() => navigate('/ratings')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{loading ? '...' : institutions.length}</div>
              <div className="stat-label">{t('dashboard.ratedInstitutions')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{loading ? '...' : analysts.length}</div>
              <div className="stat-label">{t('dashboard.analysts')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{loading ? '...' : teamMembers.length}</div>
              <div className="stat-label">{t('dashboard.teamMembers')}</div>
            </div>
            {pendingApprovals.length > 0 && (
              <div
                className="stat-card warning"
                onClick={() => navigate('/admin/users/pending')}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value">{pendingApprovals.length}</div>
                <div className="stat-label">{t('dashboard.pendingApprovals')}</div>
              </div>
            )}
          </div>
        </div>

        {/* Report Workflow Stats */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">{t('dashboard.reportWorkflow')}</h2>
            <button
              className="section-action"
              onClick={() => navigate('/reports')}
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="stats-grid">
            <div
              className="stat-card"
              onClick={() => navigate('/reports')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{loading ? '...' : draftCount}</div>
              <div className="stat-label">{t('dashboard.activeDrafts')}</div>
            </div>
            <div
              className={`stat-card ${pendingAdminCount > 0 ? 'highlight' : ''}`}
              onClick={() => navigate('/reports')}
              style={{ cursor: 'pointer' }}
            >
              <div className="stat-value">{loading ? '...' : pendingAdminCount}</div>
              <div className="stat-label">{t('dashboard.pendingAdminReview')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{loading ? '...' : clientReviewCount}</div>
              <div className="stat-label">{t('dashboard.clientReview')}</div>
            </div>
            {overdueReports.length > 0 && (
              <div
                className="stat-card danger"
                onClick={() => navigate('/reports')}
                style={{ cursor: 'pointer' }}
              >
                <div className="stat-value">{overdueReports.length}</div>
                <div className="stat-label">{t('dashboard.overdue')}</div>
              </div>
            )}
          </div>
        </div>

        {/* Institutions List */}
        <div className="dashboard-section">
          <div className="section-header">
            <h2 className="section-title">{t('dashboard.ratedInstitutions')}</h2>
            <button
              className="section-action"
              onClick={() => navigate('/ratings')}
            >
              {t('common.viewAll')}
            </button>
          </div>
          <div className="institution-list">
            {institutions.slice(0, 5).map((inst) => (
              <div
                key={inst.id}
                className="institution-item"
                onClick={() => navigate(`/analyst-bench/${inst.id}`)}
              >
                <div className={`institution-rating ${getRatingClass(inst.rating)}`}>
                  {inst.rating}
                </div>
                <div className="institution-info">
                  <div className="institution-name">{inst.name}</div>
                  <div className="institution-score">{t('dashboard.score', { value: inst.score.toFixed(1) })}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Team Members */}
        {teamMembers.length > 0 && (
          <div className="dashboard-section">
            <h2 className="section-title">{t('dashboard.teamMembers')}</h2>
            <div className="pending-list">
              {teamMembers.slice(0, 5).map((user) => (
                <div key={user.uid} className="pending-item">
                  <div className="pending-avatar">
                    {user.display_name?.[0]?.toUpperCase() || '?'}
                  </div>
                  <div className="pending-info">
                    <div className="pending-name">{user.display_name || user.email}</div>
                    <div className="pending-email">{user.email}</div>
                  </div>
                  <div className="pending-time" style={{ textTransform: 'capitalize' }}>
                    {user.role?.replace('cra_', '').replace('_', ' ')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
